import { APIEntity, dataManager, registerEntity, type AnyEntity } from '../APIEntity';
import { IEntity, EntityMerge } from '../IEntity';
import { ActionInfo } from '../models';
import { IDockPointer } from '../models/DockPointer';
import { dataContext } from '../FlowSync/context';
import { ViewType } from '../utils/ui/view-types';
import { isValidEntityId, TypeId } from '../models/TypeId';
import { EntityTypes } from '../schema/types';
import { parseScopeFilterKey, pinnedProjectId } from '../utils/scope-filter';
import { isHubOnly } from '../utils/hub-runtime';
import { normalizeRetiredDockPointer } from '../utils/ui/retired-views';
import { normalizeWorldViewDockPointer } from '../worldview/dock-pointer';
import { Project } from './project';

/** A terminal target's display fields, read off the (cached/resolved) entity. */
interface TerminalTargetFields {
  worker_type?: string | null;
  cliOptions?: { worktree?: boolean } | null;
}

/** Provider/display kind for a terminal chip (vendor→glyph) denormalized onto
 *  `Tab.icon_key`. Shells use `'shell'`; an AgenticProcess maps its
 *  `worker_type`; unset/unknown uses the generic process presentation. Runtime
 *  defaults are resolved by the backend `harness` capability at creation. */
function providerKindForWorkerType(workerType: string | null | undefined): string {
  const wt = (workerType ?? '').toLowerCase();
  if (wt === 'codex') return 'codex';
  if (wt === 'copilot') return 'copilot';
  if (wt === 'opencode') return 'opencode';
  return 'claude';
}

/** CREATE-only chip primitives (provider glyph + worktree badge), derived from
 *  the tab's target entity so the strip draws without fetching it. */
function displayForTarget(
  targetType: string | null,
  target: TerminalTargetFields | null,
): { iconKey: string | null; worktree: boolean } {
  if (targetType === EntityTypes.Shell) return { iconKey: 'shell', worktree: false };
  if (targetType === EntityTypes.AgenticProcess) {
    return {
      iconKey: providerKindForWorkerType(target?.worker_type),
      worktree: Boolean(target?.cliOptions?.worktree),
    };
  }
  return { iconKey: null, worktree: false };
}

const LEGACY_LAYOUT_SEGMENTS = new Set(['dock', 'dev', 'win']);

function targetDockPointer(
  targetType: string | null | undefined,
  targetId: string | null | undefined,
): {
  viewType: string;
  pointer: string;
} | null {
  if (!targetType || !targetId) return null;
  if (targetType === EntityTypes.Shell) {
    return { viewType: EntityTypes.Shell, pointer: `${EntityTypes.Shell}-${targetId}` };
  }
  if (targetType === EntityTypes.AgenticProcess) {
    return { viewType: EntityTypes.Shell, pointer: `${EntityTypes.AgenticProcess}-${targetId}` };
  }
  return { viewType: targetType, pointer: targetId };
}

function normalizeLegacyPointer(
  pointer: string,
  targetType: string | null | undefined,
  targetId: string | null | undefined,
): { viewType: string; pointer: string } | null {
  const separator = pointer.indexOf('|');
  const rawViewType = separator >= 0 ? pointer.slice(0, separator) : pointer;
  const rawSubPointer = separator >= 0 ? pointer.slice(separator + 1) : '';
  const targetDock = targetDockPointer(targetType, targetId);

  const fromPath = (rawPath: string): { viewType: string; pointer: string } | null => {
    const parts = rawPath.replace(/^\/+/, '').split('/').filter(Boolean);
    if (parts.length === 0) return targetDock;
    if (LEGACY_LAYOUT_SEGMENTS.has(parts[0])) parts.shift();
    const path = parts.join('/');
    if (!path) return targetDock;

    if (
      targetDock &&
      (path === targetDock.pointer || path === `${targetType}-${targetId}` || path === `${targetType}/${targetId}`)
    ) {
      return targetDock;
    }

    if (path.startsWith(`${EntityTypes.Shell}-`) || path.startsWith(`${EntityTypes.AgenticProcess}-`)) {
      return { viewType: EntityTypes.Shell, pointer: path };
    }

    const slash = path.indexOf('/');
    if (slash >= 0) {
      return { viewType: path.slice(0, slash), pointer: path.slice(slash + 1) };
    }
    return { viewType: path, pointer: '' };
  };

  if (LEGACY_LAYOUT_SEGMENTS.has(rawViewType)) {
    return fromPath(rawSubPointer);
  }
  if (rawViewType.includes('/')) {
    return fromPath(rawViewType);
  }
  if (!rawViewType && targetDock) {
    return targetDock;
  }
  if (!rawViewType) return null;
  return { viewType: rawViewType, pointer: rawSubPointer };
}

function withCanonicalTabHash(pointer: IDockPointer): IDockPointer {
  const normalized = normalizeWorldViewDockPointer(normalizeRetiredDockPointer(pointer));
  const viewType = normalized.viewType ?? '';
  const subPointer = normalized.pointer ?? '';
  return {
    ...normalized,
    tabHash: normalized.tabHash ?? `${viewType}|${subPointer}`,
  };
}

/**
 * Tab — the frontend mirror of the DB-only backend ``Tab`` entity
 * (flow_sdk/builtin/tab.py, docs/tab-management.md).
 *
 * A tab is a first-class placement row keyed by a hash of the DockPointer it
 * opens (``pointer`` == ``DockPointer.tabHash``). Every ``openDock`` ensures one
 * exists and is visible; closing flips ``visible=false`` (soft — the row
 * survives and the change broadcasts cross-client). The strip is one live query
 * of ``visible=true`` Tabs.
 *
 * Identity/dedup is the ``pointer`` field — ``ensureFor`` reconciles by querying
 * it, so two opens of the same pointer reuse one row regardless of which side
 * (frontend uuid4 / backend uuid5) minted the id. Active-tab is NOT a field
 * here: it stays URL-first/per-client (a synced active flag would yank focus
 * across clients).
 */
export interface ITab extends IEntity {
  /** Canonical DockPointer serialization — the natural key (DockPointer.tabHash). */
  pointer?: string;
  /** Denormalized target identity (off the pointer) for fast reverse lookup. */
  target_type?: string | null;
  target_id?: string | null;
  /** Advisory grouping edge to the opener tab (see backend Tab.parent_tab_id).
   *  Never affects ordering/close/recency; children stay ordinary global tabs. */
  parent_tab_id?: string | null;
  /** Visibility = membership. Non-null; a close broadcasts ``visible=false``. */
  visible?: boolean;
  /**
   * Display primitives the strip draws straight off the Tab (no Shell/AP fetch).
   * Both CREATE-only + static: ``icon_key`` is the resolved provider kind
   * (`shell`/`claude`/`codex`/`copilot`), ``worktree`` drives the badge.
   * (Named ``icon_key`` not ``icon`` — the base entity owns an ``icon`` getter.)
   */
  icon_key?: string | null;
  worktree?: boolean;
  /** Runtime-computed fields: backing entity status and close-in-progress flag. Never persisted. */
  status?: string | null;
  is_disabled?: boolean;
  target_remote?: boolean;
  /** The content target's own project (API-only); differs from `project_id` on a guest tab. */
  target_project_id?: string | null;
  /** name / project_id / tab_order / last_active_at come from IEntity. */
}

export interface IEnsureTabOpts {
  targetType?: string | null;
  targetId?: string | null;
  projectId?: string | null;
  name?: string | null;
  iconKey?: string | null;
  worktree?: boolean;
  /** Mark the new/reopened tab a child of this tab (the opener). See ITab. */
  parentTabId?: string | null;
}

/** A get-or-create's render list, and whether the call minted the tab's row. */
export interface TabEnsureResult {
  tabs: Tab[];
  created: boolean;
}

export interface INewTabOpts extends IEnsureTabOpts {
  /** The opener (current active tab id): a fresh tab lands right after it. */
  afterTabId?: string | null;
}

/** Backward-compatible name for older call sites/tests during the Tab migration. */
export interface TabRow extends ITab {
  id: string;
  pointer: string;
  target_type: string | null;
  target_id: string | null;
  parent_tab_id: string | null;
  project_id: string | null;
  name: string | null;
  icon_key: string | null;
  worktree: boolean;
  tab_order: number;
  last_active_at: number | string | null;
  status: string | null;
  is_disabled: boolean;
  target_remote?: boolean;
}

// `implements ITab` only checks the class; it contributes no members, so every
// field declared solely on ITab read as "does not exist". deepAssign populates
// them from the wire — this merge makes them part of the class type.
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface Tab extends EntityMerge<ITab> {}

/** The fields `resolveTabProject` reads off a dock's resolved target entity. */
export type TabProjectTarget = {
  typeId?: TypeId | null;
  project_id?: string | null;
  effective_project_id?: string | null;
  cwd?: string | null;
  workdir?: string | null;
} | null;

/** A content sub-pointer (an editor or wiki page) names ONE document, never a browse
 *  scope. Mirrors the backend's `_is_content_sub_pointer`. */
export function isContentSubPointer(pointer: string | null | undefined): boolean {
  return /^(editor|wiki)\//.test(pointer ?? '');
}

/**
 * `[scopeKeyed, project]` read off a tab hash — the backend's `_pointer_scope_project`:
 * a scope-keyed row (`assets|<scopeFilterKey>`, optionally `|@<host>`) belongs to the
 * project its scope pins; every other scope is Global.
 */
function tabHashScopeProject(tabHash: string | null | undefined): [boolean, string | null] {
  const hash = (tabHash ?? '').split('|@', 1)[0];
  if (!hash.includes('|')) return [false, null];
  const scope = parseScopeFilterKey(hash.slice(hash.lastIndexOf('|') + 1));
  if (!scope) return [false, null];
  const pinned = pinnedProjectId(scope);
  return [true, pinned && isValidEntityId(pinned) ? pinned : null];
}

/** The live project ids when the project list is already cached — no request; null
 *  when it is not loaded yet. */
async function cachedProjectIds(): Promise<Set<string> | null> {
  const { lazyAssets, LazyAsset } = await import('../lazy');
  const projects = lazyAssets.client.getQueryData<Project[]>(lazyAssets.key(LazyAsset.Projects));
  return projects ? new Set(projects.map((p) => p.id)) : null;
}

/** A project id named by a URL/pointer (steps 1, 3), or null when it cannot be one:
 *  checked against the cached project list like the backend's existence check, and
 *  by shape alone while that list isn't loaded. */
async function namedProject(id: string | null | undefined): Promise<string | null> {
  if (!id || !isValidEntityId(id)) return null;
  const live = await cachedProjectIds();
  return !live || live.has(id) ? id : null;
}

/**
 * The asset's OWN project — `resolveTabProject`'s steps 2, 5, 6 (the loader's redirect
 * target, the tab's `target_project_id`); mirrors the backend's `tab_owner_project`.
 */
export async function tabOwnerProject(dock: IDockPointer, target: TabProjectTarget): Promise<string | null> {
  const targetTypeId = dock.targetTypeId ?? target?.typeId ?? null;
  // 2
  if (targetTypeId?.type === EntityTypes.Project) return targetTypeId.id;
  // 5
  const own = target?.project_id ?? target?.effective_project_id ?? null;
  if (own) return own;
  // 6 — an unminted file still sits inside a project folder.
  for (const path of [dock.vfsPath?.machinePath, target?.cwd ?? target?.workdir]) {
    if (!path) continue;
    const byPath = await Project.getProjectByPath(path);
    if (byPath) return byPath.id;
  }
  return null;
}

/**
 * The project a dock's tab belongs to — THE rule order, mirrored by the backend's
 * `resolve_tab_project` (docs/tab-management.md §tab project):
 *
 *   1. dependency home              → its consumer project
 *   2. Project target / landing     → that project
 *   3. presenting project in URL    → `/dock/project/<P>/…` (set only by `flow show`
 *                                     / presentation; a guest tab when ≠ the asset's)
 *   4. scope-keyed BROWSE row       → its scope's project (`all` → Global); an
 *                                     editor/wiki sub-pointer is content, not browse
 *   5. target entity                → its (effective) project
 *   6. path (vfs, then cwd/workdir) → the deepest project mount holding it
 *   7. the project it was opened in (the active project) — only for a target-less
 *      dock, a target that no longer resolves, or a plain shell; an existing
 *      projectless target is Global.
 *
 * Steps 2, 5, 6 alone are `tabOwnerProject`. Cache-first: project checks read the
 * cached project list, `target` is passed in resolved.
 */
export async function resolveTabProject(dock: IDockPointer, target: TabProjectTarget): Promise<string | null> {
  const targetTypeId = dock.targetTypeId ?? target?.typeId ?? null;
  // 1
  const consumer = dock.options?.providerProject ? await namedProject(dock.options.consumerProject) : null;
  if (consumer) return consumer;
  // 2
  if (targetTypeId?.type === EntityTypes.Project) return targetTypeId.id;
  // 3
  const named = dock.viewType === ViewType.PROJECT && dock.pointer ? dock.pointer.split('/', 1)[0] : dock.presentedIn;
  const presenting = named ? await namedProject(named) : null;
  if (presenting) return presenting;
  // 4
  const [scopeKeyed, scopeProject] = tabHashScopeProject(dock.tabHash);
  if (scopeKeyed && !isContentSubPointer(dock.pointer)) return scopeProject;
  // 5, 6
  const owner = await tabOwnerProject(dock, target);
  if (owner) return owner;
  // 7
  const takesOpenedIn = !targetTypeId || !target || targetTypeId.type === EntityTypes.Shell;
  return takesOpenedIn ? (dataContext.project?.id ?? null) : null;
}

@registerEntity
export class Tab extends APIEntity<Tab> implements ITab {
  static type: string = 'tab';

  pointer: string = '';
  target_type: string | null = null;
  target_id: string | null = null;
  parent_tab_id: string | null = null;
  visible: boolean = true;
  icon_key: string | null = null;
  worktree: boolean = false;
  name: string | null = null;
  project_id: string | null = null;
  tab_order: number = 0;
  last_active_at: number | string | null = null;
  status: string | null = null;
  is_disabled: boolean = false;
  target_remote?: boolean;
  target_project_id?: string | null;

  /** Computed DockPointer from the stored pointer. Parsed directly (SDK layer, no UI dependency). */
  get dockPointer(): IDockPointer | null {
    if (!this.pointer) return null;
    try {
      const parsed = JSON.parse(this.pointer) as IDockPointer;
      return withCanonicalTabHash(parsed);
    } catch {
      const normalized = normalizeLegacyPointer(this.pointer, this.target_type, this.target_id);
      if (!normalized) return null;
      return withCanonicalTabHash(normalized as IDockPointer);
    }
  }

  /** Get the key used for chip identity (tabHash or fallback to id). */
  getKey(): string {
    return this.dockPointer?.tabHash ?? this.id;
  }

  // ── Backend-owned ordered list (the single render source) ───────────────────
  // These call the collection-level `tab` actions (no entity id) and return the
  // canonical, project-filtered, ordered rows. The frontend renders them as-is.

  /** GET /graph/tab/list?project=<id> — the deterministic ordered render list for
   *  one exact project scope. `null` selects the separate Global scope. */
  static async list(projectId: string | null = null): Promise<Tab[]> {
    const info = new ActionInfo('list', Tab.type, null, 'GET');
    info.queryParameters = { project: projectId ?? '' };
    const res = await dataManager.callAction<undefined, { tabs: ITab[] }>(info);
    return Tab.fromResponse(res?.tabs ?? []);
  }

  /** POST /graph/tab/new_tab — loader-driven get-or-create. A fresh tab lands
   *  right after `opts.afterTabId` (the opener); reopen keeps its slot. Returns
   *  the updated list. */
  static async newTab(pointer: string, opts: INewTabOpts = {}): Promise<Tab[]> {
    return (await Tab.ensure(pointer, opts)).tabs;
  }

  /** `newTab`, plus whether the call minted the row (`created`). */
  static async ensure(pointer: string, opts: INewTabOpts = {}): Promise<TabEnsureResult> {
    const info = new ActionInfo('new_tab', Tab.type, null, 'POST');
    info.bodyParameters = {
      pointer,
      target_type: opts.targetType ?? null,
      target_id: opts.targetId ?? null,
      project_id: opts.projectId ?? null,
      name: opts.name ?? null,
      icon_key: opts.iconKey ?? null,
      worktree: opts.worktree ?? false,
      after_tab_id: opts.afterTabId ?? null,
      parent_tab_id: opts.parentTabId ?? null,
    };
    const res = await dataManager.callAction<unknown, { tabs: ITab[]; created?: boolean }>(info);
    return { tabs: Tab.fromResponse(res?.tabs ?? []), created: res?.created ?? false };
  }

  /**
   * THE tab chokepoint: get-or-create the Tab for a dock, with `project_id`
   * resolved from the dock's TARGET — never the ambient active project (that
   * re-parented tabs on a cross-project open). DockPointer stays a pure string
   * manipulator; all network/DB resolution lives here.
   *
   * Self-sufficient (cache-first, network fallback) so it's correct regardless
   * of call site: an entity dock resolves via `getByTypeId`; a vfs asset dock
   * via `getEntityByPath` (the pure, no-recovery path lookup). `project_id`: a
   * project tab belongs to its OWN id; otherwise the target entity's
   * `project_id`; a target-less dock → null. No-tab docks (home, bare shell)
   * return [].
   */
  /**
   * Resolve a dock's TARGET entity and the `project_id` its tab must carry —
   * the single resolution `getFromDockPointer` persists and callers (e.g. the
   * lens staleness gate in `materializeTab`) compare against. Cache-first: an
   * entity dock resolves via `getByTypeId`; a vfs asset dock via
   * `getEntityByPath` (the pure, no-recovery path lookup).
   */
  static async resolveDockTarget(dock: IDockPointer): Promise<{
    targetTypeId: TypeId | null;
    target:
      | (AnyEntity &
          TerminalTargetFields & { project_id?: string | null; cwd?: string | null; workdir?: string | null })
      | null;
    projectId: string | null;
  }> {
    // One cast names the fields we read off the heterogeneous target
    // (project + terminal-display).
    let targetTypeId = dock.targetTypeId ?? null;
    const target = (
      targetTypeId
        ? await dataManager.getByTypeId<AnyEntity>(targetTypeId).catch(() => null)
        : dock.vfsPath
          ? await dataManager.getEntityByPath<AnyEntity>(dock.vfsPath.machinePath)
          : null
    ) as
      | (AnyEntity &
          TerminalTargetFields & { project_id?: string | null; cwd?: string | null; workdir?: string | null })
      | null;
    if (!targetTypeId && target) targetTypeId = target.typeId;

    const projectId = await resolveTabProject(dock, target);

    return { targetTypeId, target, projectId };
  }

  static async getFromDockPointer(
    dock: IDockPointer,
    opts: { parentTabId?: string | null; afterTabId?: string | null } = {},
  ): Promise<TabEnsureResult> {
    const pointerJson = dock.toJSON?.();
    if (!pointerJson) return { tabs: [], created: false };

    const { targetTypeId, target, projectId } = await Tab.resolveDockTarget(dock);

    const { iconKey, worktree } = displayForTarget(targetTypeId?.type ?? null, target);

    return Tab.ensure(pointerJson, {
      targetType: targetTypeId?.type ?? null,
      targetId: targetTypeId?.id ?? null,
      projectId,
      name: dataManager.getTabName(dock),
      iconKey,
      worktree,
      parentTabId: opts.parentTabId ?? null,
      // Explicit placement anchor. Omitted, the backend appends after the
      // most-recently-active tab (browser-style opener locality), which is
      // right for a navigation the user just made. A tab minted WITHOUT
      // navigating (`flow show` outside vibe) has no such opener, so it names
      // the tab it belongs beside.
      afterTabId: opts.afterTabId ?? null,
    });
  }

  /** GET /graph/tab/list_all — EVERY visible Tab (any kind, ALL projects), fully
   *  resolved, in global order. The single unscoped projection that the developer
   *  sessions view + footer projects-chip read; replaces the old reactive
   *  `tab?visible=true` entity query. (Project-scoped views use `list`.) */
  static async listAll(): Promise<Tab[]> {
    // Hub mode runs ephemeral: the hub backend has no `tab` entity, so a
    // `list_all` would 422. Return an empty set — tabs are never persisted here.
    if (isHubOnly()) return [];
    const info = new ActionInfo('list_all', Tab.type, null, 'GET');
    const res = await dataManager.callAction<undefined, { tabs: ITab[] }>(info);
    return Tab.fromResponse(res?.tabs ?? []);
  }

  /** POST /graph/tab/order — drag-drop commit. Splices `reorderId` into the
   *  drop-gap (after `afterId` / before `beforeId`) within the global order and
   *  returns the updated project-filtered list. No-op ⇒ unchanged list. */
  static async reorder(
    reorderId: string,
    afterId: string | null,
    beforeId: string | null,
    projectId: string | null = null,
  ): Promise<Tab[]> {
    const info = new ActionInfo('order', Tab.type, null, 'POST');
    info.bodyParameters = {
      reorder_tab_id: reorderId,
      after_tab_id: afterId,
      before_tab_id: beforeId,
      project: projectId ?? '',
    };
    const res = await dataManager.callAction<unknown, { tabs: ITab[] }>(info);
    return Tab.fromResponse(res?.tabs ?? []);
  }

  // Action-by-id helpers — invoke a backend Tab action WITHOUT constructing a
  // throwaway `new Tab({id})`. The strip renders from plain Tab data (not
  // entities), so a fresh `Tab` with an already-cached id collides in the entity
  // store ("already registered with different entity"). These call the action by
  // id directly and return the canonical list.

  /** POST /graph/tab/<id>/close — soft-close (backend flips visible + dispatches
   *  target teardown). Returns the updated list. */
  static async closeById(id: string): Promise<Tab[]> {
    const info = new ActionInfo('close', Tab.type, id, 'POST');
    // Optimistic close: see ActionInfo.keepalive.
    info.keepalive = true;
    const res = await dataManager.callAction<unknown, { tabs: ITab[] }>(info);
    return Tab.fromResponse(res?.tabs ?? []);
  }

  /** POST /graph/tab/close_many — durably hide all requested tabs in one
   * acknowledgement. Used by close-all so a reload cannot abort a fan-out of
   * independent requests after the chips have disappeared. */
  static async closeManyByIds(ids: string[], projectId: string | null = null): Promise<Tab[]> {
    const info = new ActionInfo('close_many', Tab.type, null, 'POST');
    info.bodyParameters = {
      tab_ids: ids,
      project: projectId ?? '',
    };
    const res = await dataManager.callAction<unknown, { tabs: ITab[] }>(info);
    return Tab.fromResponse(res?.tabs ?? []);
  }

  /** POST /graph/tab/<id>/rename {name} — the backend reflects onto the backing
   *  entity via generic `Entity.rename`. Returns the updated list. */
  static async renameById(id: string, name: string): Promise<Tab[]> {
    const info = new ActionInfo('rename', Tab.type, id, 'POST');
    info.bodyParameters = { name };
    const res = await dataManager.callAction<{ name: string }, { tabs: ITab[] }>(info);
    return Tab.fromResponse(res?.tabs ?? []);
  }

  /** POST /graph/tab/<id>/activate — stamp recency (resolver seed for opener /
   *  close-to-last-active). */
  static async activateById(id: string): Promise<void> {
    const info = new ActionInfo('activate', Tab.type, id, 'POST');
    await dataManager.callAction<undefined, unknown>(info);
  }

  /** POST /graph/tab/<id>/set_name {name} — set ONLY the Tab label (no entity
   *  reflect, no `auto_rename` change). The PTY auto-title mirror: the active panel
   *  already saved the live name onto its Shell/AgenticProcess; this keeps the
   *  durable `Tab.name` in step so the chip stays right once inactive. NOT `rename`
   *  (which would pin `auto_rename=false` and stop future auto-titles). */
  static async setNameById(id: string, name: string): Promise<Tab[]> {
    const info = new ActionInfo('set_name', Tab.type, id, 'POST');
    info.bodyParameters = { name };
    const res = await dataManager.callAction<{ name: string }, { tabs: ITab[] }>(info);
    return Tab.fromResponse(res?.tabs ?? []);
  }

  /** A closed tab: `visible=false` is the whole of "closed" (the row is never deleted). */
  override isRetired(): boolean {
    return this.visible === false;
  }

  /** Instance soft-close (also updates the local flag). */
  async closeTab(): Promise<Tab[]> {
    const rows = await Tab.closeById(this.id);
    this.visible = false;
    return rows;
  }

  /** Instance rename (also updates the local name). */
  async rename(name: string): Promise<Tab[]> {
    const rows = await Tab.renameById(this.id, name);
    this.name = name;
    return rows;
  }

  /** Factory to deserialize Tab array from API response. Uses entity cache
   *  to reuse instances by id, preventing duplicate-registration warnings. */
  static fromResponse(data: ITab[]): Tab[] {
    return data.map((t) => {
      const targetRemote =
        typeof t.target_remote === 'boolean' ? t.target_remote : undefined;
      const cached = Tab.getByIdFromCache<Tab>(t.id ?? '');
      const tab = cached ?? new Tab(t);
      Object.assign(tab, t);
      tab.target_remote = targetRemote;
      tab.target_project_id = t.target_project_id ?? null;
      return tab;
    });
  }

  constructor(entity: Partial<ITab> = {}) {
    super(entity as IEntity);
    Object.assign(this, entity);
  }
}
